<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('systemMessages.systemMessages') }}</span>
        <h3 class="page-title">{{ $t('systemMessages.editMessage') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/komunikaty-systemowe')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          theme="success"
          :disabled="isSubmitting || !loadedData"
          @click.prevent="validateForm">
          <i class="material-icons">edit</i>
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
    </d-alert>

    <!-- Form -->
    <d-form
      v-else
      :class="{ 'is-loading': !allDataLoaded }">
      <d-card class="mb-5">
        <d-tabs
          v-sticky-tabs
          card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer
              ref="system-message-edit-form"
              tag="form"
              v-slot="{ validated }">

              <div class="form-group">
                <label for="title">
                  {{ $t('ui.title') }}:
                </label>
                <validation-provider
                  rules="required"
                  name="title"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    :disabled="isSubmitting"
                    v-model="title" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="content">
                  {{ $t('ui.content') }}:
                </label>
                <validation-provider
                  rules="required"
                  name="content"
                  v-slot="{ errors }">
                  <d-textarea
                    v-model="content"
                    :value.prop="content"
                    :state="errors.length ? 'invalid' : null"
                    :rows="5"
                    :disabled="isSubmitting" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <hr class="my-4">

              <h5 class="mb-3">{{ $t('users.usersLabel') }}</h5>

              <div class="form-group mb-4">
                <d-checkbox
                  inline
                  v-model="isSelectAllUsers"
                  toggle
                  :disabled="isSubmitting || !allDataLoaded">
                  {{ $t('systemMessages.sendToAllUsers') }}
                </d-checkbox>
              </div>

              <div v-show="!isSelectAllUsers">
                <div class="form-group d-flex">
                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      id="users_select"
                      v-model="selectedUserToAdd"
                      :options="availableUsersToSelect"
                      :placeholder="$t('users.selectWorker')"
                      :disabled="!loadedUsers || isSubmitting"
                      label="name">
                      <template slot="option" slot-scope="option">
                        <div class="d-flex justify-content-between align-items-center w-100">
                          <span>{{ option.name }}</span>
                          <span class="text-muted small" v-if="option.email">{{ option.email }}</span>
                        </div>
                      </template>
                    </v-select>
                  </div>

                  <d-button
                    theme="success"
                    :disabled="!selectedUserToAdd || isSubmitting"
                    @click.prevent="addUser"
                    class="ml-2 w-25">
                    {{ $t('ui.add') }}
                  </d-button>
                </div>

                <d-alert
                  :show="!selectedUsers.length"
                  theme="info"
                  class="mt-3">
                  {{ $t('systemMessages.noUsersSelected') }}
                </d-alert>

                <table
                  v-if="selectedUsers.length"
                  class="table mt-3">
                  <tbody>
                    <tr
                      v-for="(user, index) of selectedUsers"
                      :key="'selected-user-' + index">
                      <td class="align-middle">
                        {{ user.name }}
                        <span class="text-muted small ml-1">
                          ({{ getUserTypeName(user.type) }}
                          <template v-if="user.email">
                            , {{ user.email }}
                          </template>)
                        </span>
                      </td>
                      <td
                        style="width: 70px;"
                        class="align-middle">
                        <d-button
                          theme="danger"
                          :disabled="isSubmitting"
                          @click.native.prevent="removeUser(user.id)"
                          :title="$t('ui.delete')">
                          <i class="material-icons">delete</i>
                        </d-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <hr class="my-4">

              <div class="form-group mt-4">
                <h5 class="mb-3">{{ $t('settings.userGroups.userGroups') }}</h5>

                <ul class="user-groups-list">
                  <li
                    v-for="(group, index) of userGroups"
                    :key="'user-group-' + index">
                    <d-checkbox
                      inline
                      v-model="selectedGroups[group.id]"
                      :disabled="isSubmitting">
                      {{ group.name }}
                    </d-checkbox>
                  </li>
                </ul>
              </div>

              <div
                v-if="validated && !formUsers.length && !formGroups.length"
                class="invalid-feedback d-block">
                {{ $t('systemMessages.usersGroupsValidationError') }}
              </div>

            </validation-observer>
          </d-tab>
        </d-tabs>

        <div v-if="!allDataLoaded && !loadError" class="pb-3 pt-3 text-center overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from '@/utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'system-message-edit',
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedUserGroups && this.loadedUsers;
    },
    availableUsersToSelect () {
      const selectedIds = this.selectedUsers.map(item => item.id);
      return this.allUsers.filter(item => !selectedIds.includes(item.id));
    },
    formUsers () {
      return this.selectedUsers.map(item => item.id);
    },
    formGroups () {
      return Object.keys(this.selectedGroups).filter(id => this.selectedGroups[id]);
    }
  },
  watch: {
    allDataLoaded (newValue) {
      if (newValue) {
        if (this.selectedUsers.length > 0 && this.selectedUsers.length === this.allUsers.length) {
          this.isSelectAllUsers = true;
        }
      }
    },
    isSelectAllUsers (newValue) {
      if (!this.allDataLoaded) {
        return;
      }

      this.selectedUsers = newValue ? [...this.allUsers] : [];
    }
  },
  data () {
    return {
      id: null,
      content: '',
      title: '',
      users: [],
      groups: [],
      selectedGroups: {},
      selectedUsers: [],
      selectedUserToAdd: null,
      isSelectAllUsers: false,
      userGroups: [],
      allUsers: [],
      loadedData: false,
      loadedUserGroups: false,
      loadedUsers: false,
      loadError: false,
      isSubmitting: false
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.loadItemData();
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/messages/item/',
        instanceFields: ['id', 'content', 'title', 'users', 'groups'],
        loadedKey: 'loadedData',
        errorKey: 'loadError',
        successAction: () => {
          this.hydrateRecipients();
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/groups/items',
        loadedKey: 'loadedUserGroups',
        outputKey: 'userGroups',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        loadedKey: 'loadedUsers',
        outputKey: 'allUsers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            type: ['admin', 'worker', 'warehouse_manager', 'warehouseman']
          }
        }
      });
    },
    hydrateRecipients () {
      this.selectedUsers = (this.users || [])
        .map(item => item.user)
        .filter(Boolean);

      const groupsMap = {};
      (this.groups || []).forEach(group => {
        groupsMap[group.id] = true;
      });
      this.selectedGroups = groupsMap;
    },
    addUser () {
      if (this.selectedUserToAdd) {
        this.selectedUsers.push(this.selectedUserToAdd);

        Vue.nextTick(() => {
          this.selectedUserToAdd = null;
        });
      }
    },
    removeUser (userId) {
      this.selectedUsers = this.selectedUsers.filter(item => item.id !== userId);
    },
    validateForm () {
      if (this.isSubmitting) {
        return;
      }
      if (!this.formUsers.length && !this.formGroups.length) {
        FormUtils.validate(this.$refs['system-message-edit-form'], () => {});
        return;
      }
      FormUtils.validate(this.$refs['system-message-edit-form'], this.submitForm);
    },
    submitForm () {
      this.isSubmitting = true;
      const formDataToSend = this.prepareFormData();

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/messages/update/' + this.$route.params.id,
        formData: formDataToSend,
        successTitle: this.$t('systemMessages.editMessageSuccessTitle'),
        successText: this.$t('systemMessages.editMessageSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('systemMessages.editMessageErrorText'),
        successAction: () => {
          this.isSubmitting = false;
          this.$router.push('/ustawienia/komunikaty-systemowe');
        },
        failAction: () => {
          this.isSubmitting = false;
        }
      });
    },
    prepareFormData () {
      let formData = {
        content: this.content,
        title: this.title,
        groups: this.formGroups,
        users: this.formUsers
      };

      return formData;
    },
    getUserTypeName (userType) {
      if (userType === 'warehouseman') {
        return this.$t('users.realWarehouseman')
      }
      if (userType === 'warehouse_manager') {
        return this.$t('users.warehouseman')
      }

      return this.$t(`users.${userType}`)
    }
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}

.user-groups-list {
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  list-style-type: none;
  margin: 0;
  padding: 20px 20px 10px 20px;

  li {
    font-size: 12px;
  }
}
</style>
